import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas-pro';
import type { TemplateConfig } from '@/mocks/template-config';
import type { ProposalMock } from '@/mocks/proposals';
import type { PDFExportConfig } from '@/mocks/pdf-export';

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;

/**
 * Exporta uma proposta como PDF.
 * Cada pagina e um container A4 fixo (297mm).
 * Rodape sempre fixo na parte inferior.
 * Nunca cria paginas vazias.
 */
export async function exportProposalPDF(
  element: HTMLElement,
  proposal: ProposalMock,
  _templateConfig: TemplateConfig,
  hasAdvantages = false,
  pdfConfig?: PDFExportConfig
): Promise<void> {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageIds: string[] = ['pdf-page-cover'];
  if (hasAdvantages) {
    pageIds.push('pdf-page-vantagens');
  }
  pageIds.push('pdf-page-content');
  pageIds.push('pdf-page-backcover');

  let firstPage = true;

  for (const pageId of pageIds) {
    const pageEl = element.querySelector(`#${pageId}`) as HTMLElement | null;
    if (!pageEl) continue;

    // Aguardar imagens carregadas
    const imgs = pageEl.querySelectorAll('img');
    await Promise.all(
      Array.from(imgs).map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete) {
              resolve();
            } else {
              img.onload = () => resolve();
              img.onerror = () => resolve();
            }
          })
      )
    );

    const isCoverPage = pageId === 'pdf-page-cover' || pageId === 'pdf-page-backcover';
    const bgColor = isCoverPage ? '#0a1628' : '#f5f7fa';

    // Forcar html2canvas a capturar EXATAMENTE a altura do elemento (297mm)
    // Isso evita que conteudo transbordado seja capturado
    const captureHeight = Math.max(1, Math.round(pageEl.offsetHeight));

    const canvas = await html2canvas(pageEl, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: bgColor,
      height: captureHeight,
    });

    // Pular conteudo invisivel ou muito pequeno
    if (canvas.height < 20) {
      continue;
    }

    const imgData = canvas.toDataURL('image/jpeg', 0.92);
    const imgWidthMM = A4_WIDTH_MM;
    const imgHeightMM = (canvas.height * imgWidthMM) / canvas.width;

    if (!firstPage) {
      pdf.addPage();
    }
    firstPage = false;

    if (isCoverPage) {
      // Capa e contra-capa: preenchem a pagina A4 inteira
      pdf.addImage(imgData, 'JPEG', 0, 0, A4_WIDTH_MM, A4_HEIGHT_MM);
    } else {
      // PAGINAS INTERNAS: altura real do canvas, nunca esticar
      // Se o canvas for exatamente A4 (ou muito proximo), adicionar como A4
      // Se for maior (caso raro), fatiar em slices de A4
      const heightTolerance = 5; // 5mm de tolerancia

      if (imgHeightMM <= A4_HEIGHT_MM + heightTolerance) {
        // Conteudo cabe em uma pagina: altura natural
        pdf.addImage(imgData, 'JPEG', 0, 0, A4_WIDTH_MM, imgHeightMM);
      } else {
        // Conteudo maior que A4: fatiar com slices de altura A4
        const totalHeightPx = canvas.height;
        const A4_HEIGHT_PX = Math.round((A4_HEIGHT_MM * canvas.width) / A4_WIDTH_MM);
        let currentY = 0;

        while (currentY < totalHeightPx) {
          const remainingPx = totalHeightPx - currentY;
          const sliceHeightPx = Math.min(A4_HEIGHT_PX, remainingPx);

          // Se o ultimo slice for muito pequeno (menos de 20px de canvas), ignorar
          if (sliceHeightPx < 20 && currentY > 0) {
            break;
          }

          // Criar canvas do slice
          const sliceCanvas = document.createElement('canvas');
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = sliceHeightPx;
          const sliceCtx = sliceCanvas.getContext('2d');
          if (sliceCtx) {
            sliceCtx.fillStyle = bgColor;
            sliceCtx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
            sliceCtx.drawImage(
              canvas,
              0, currentY,
              canvas.width, sliceHeightPx,
              0, 0,
              canvas.width, sliceHeightPx
            );
          }

          const sliceData = sliceCanvas.toDataURL('image/jpeg', 0.92);
          const sliceHeightMM = (sliceHeightPx * A4_WIDTH_MM) / canvas.width;
          pdf.addImage(sliceData, 'JPEG', 0, 0, A4_WIDTH_MM, sliceHeightMM);

          currentY += sliceHeightPx;

          // Se ainda houver conteudo, adicionar nova pagina
          if (currentY < totalHeightPx) {
            pdf.addPage();
          }
        }
      }
    }
  }

  // Adicionar numeracao de paginas se ativada
  const includePageNumbers = pdfConfig?.includePageNumbers ?? false;
  if (includePageNumbers) {
    const totalPages = pdf.getNumberOfPages();
    const pageNumberFontSize = 9;

    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setTextColor(201, 164, 74);
      pdf.setFontSize(pageNumberFontSize);
      pdf.setFont('helvetica', 'normal');
      pdf.text(
        `Pagina ${i} de ${totalPages}`,
        A4_WIDTH_MM / 2,
        A4_HEIGHT_MM - 8,
        { align: 'center' }
      );
    }
  }

  const proposalNumber =
    (proposal as unknown as Record<string, unknown>).proposal_number as string || proposal.id;
  const clientSlug = proposal.clientName.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
  pdf.save(`Proposta_${proposalNumber}_${clientSlug}.pdf`);
}